<template>
  <div class="container-fluid page-container">
    <ban-edit-profile v-if="isBannedFromEditing" />
    <template v-else>
      <spinner v-if="busy" />
      <b-row v-else-if="coverageError" class="vh-70">
        <b-col class="col-sm-7 m-auto">
          <b-card :body-class="['shadow', 'border', 'rounded', 'py-5', 'text-center']">
            <b-card-text>
              <h2 class="mb-0 h3 px-3 px-md-5">
                {{ $t('In order to create your company listing, you must first set up your lead types and coverage area.') }}
              </h2>
              <hr class="w-50">
              <router-link class="text-link text-link-reset" :to="{name:'lead-selection-&-coverage-tab-0'}">
                {{ $t('Set up your lead types and coverage') }}
              </router-link>
            </b-card-text>
          </b-card>
        </b-col>
      </b-row>
      <template v-else>
        <div id="listing-tooltip" class="row align-items-center page-header__container mb-4 pb-3">
          <page-heading class="col-auto" :title="pageTitle" />
          <span v-if="isMobile" class="float-left">
            <font-awesome-icon icon="info-circle" />
          </span>
        </div>
        <b-tooltip v-if="isMobile" target="listing-tooltip" triggers="hover">
          You can have as many coverage areas as you like. Use them to refine where you want specific work.
          For example, you may want to stay local for servicing but willing to travel further for full installation jobs.
          <br>
          Refine your coverage areas so that we can send you the right lead alerts. The more you buy from them, the
          more your quality score will increase with us - meaning more leads!
        </b-tooltip>
        <div class="row mx-0">
          <div class="col-12 px-0 d-flex justify-content-xxl-between">
            <b-button-group class="listing-steps">
              <div
                class="btn-light text-left first listing-steps__tab"
                :class="[step===1 ? 'listing-steps__tab--selected' : '']" @click="navigate(null, 1)"
              >
                <div class="h5 mb-0 font-weight-bold">
                  {{ $t('1. Add description') }}
                </div>
              </div>
              <div
                class="btn-light text-left listing-steps__tab"
                :class="[step===2 ? 'listing-steps__tab--selected' : '']" @click="navigate(null, 2)"
              >
                <div class="h5 mb-0 font-weight-bold">
                  {{ $t('2. Add your logo') }}
                </div>
              </div>
              <div
                class="btn-light text-left listing-steps__tab"
                :class="[step===3 ? 'listing-steps__tab--selected' : '']" @click="navigate(null, 3)"
              >
                <div class="h5 mb-0 font-weight-bold">
                  {{ $t('3. Add photos') }}
                </div>
              </div>
              <div
                class="btn-light text-left last listing-steps__tab"
                :class="[step===4 ? 'listing-steps__tab--selected' : '']" @click="navigate(null, 4)"
              >
                <div class="h5 mb-0 font-weight-bold">
                  {{ $t('4. View your Listing') }}
                </div>
              </div>
            </b-button-group>
            <listing-buttons
              :busy="busy"
              :disable-save="busy || !canSave"
              :saving-animation="isSaving"
              :back="[2,3,4].includes(step)"
              :next="[1,2,3].includes(step)"
              :save="[1,2,3].includes(step)"
              :global-save="[1].includes(step)"
              class="bg-white col-md d-md-block listing-control-bar"
              @next="navigate(true)"
              @back="navigate(false)"
              @save="save(step)"
            />
          </div>
        </div>
        <div v-if="!isMobile" class="row">
          <div class="col-12">
            <b-alert class="bg-white mt-3 mb-0 font-size-14 shadow border" :show="true">
              {{ $t('yourListing') }}<br>
              <div v-if="(listingDetails && !listingDetails.valid)" class="alert alert-warning mb-0 mt-2">
                {{ $t('Your listing is not valid and can not be displayed. Please make sure you fill in your company\'s Trading name and add a description.') }}
              </div>
              <div v-if="(!listingMasterSwitch && step !== 4)" class="alert alert-warning mb-0 mt-2">
                <i18n path="inactiveListing">
                  <template v-slot:contact>
                    <router-link class="text-link text-link-reset" :to="{path:'/help/contact'}">
                      {{ $t('contact') }}
                    </router-link>
                  </template>
                </i18n>
              </div>
            </b-alert>
          </div>
        </div>
        <div class="row">
          <div class="col-12 pt-3">
            <listing-details v-if="step === 1" ref="l1" v-model="listingDetails" @state="setState" @isSaving="setIsSaving" @canSave="setCanSave" />
            <listing-logo v-if="step === 2" ref="l2" v-model="listingDetails" @state="setState" @isSaving="setIsSaving" @canSave="setCanSave" />
            <listing-images v-if="step === 3" ref="l3" v-model="listingDetails" @state="setState" @isSaving="setIsSaving" @canSave="setCanSave" />
            <listing-links v-if="step === 4" :listing="listingDetails" />
          </div>
        </div>
      </template>
    </template>
    <b-modal id="nav-confirm" centered @ok="clean=true;navigate(null, nextTab)">
      <template #modal-header="{ close }">
        <h5 class="modal-title">
          {{ $t('Listing') }}
        </h5>
        <b-button class="close" @click.stop="close">
          <font-awesome-icon icon="times" />
        </b-button>
      </template>
      <div>{{ $t('You have unsaved changes which will be lost if you navigate away from this tab. Do you want to proceed?') }}</div>
      <template #modal-footer="{ ok, cancel }">
        <b-button size="sm" variant="outline-secondary" @click="cancel()">
          {{ $t('No') }}
        </b-button>
        <b-button size="sm" variant="primary" @click="ok()">
          {{ $t('Yes') }}
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import Spinner from "@/components/animation/Spinner";
import PageHeading from "@/components/control/PageHeading";
import ListingDetails from "@/components/tabs/listing/ListingDetails";
import ListingLogo from "@/components/tabs/listing/ListingLogo";
import ListingImages from "@/components/tabs/listing/ListingImages";
import ListingLinks from "@/components/tabs/listing/ListingLinks";
import {MAIN_LISTING} from "@/store/company/mutation-types";
import BanEditProfile from "@/components/control/BanEditProfile";
import isBannedFromEditing from "@/imports/mixins/isBannedFromEditing";
import ListingButtons from "@/components/control/ListingButtons";
import userDevice from "@/imports/mixins/userDevice";

export default {
  name: "Listing",
  components: {ListingLinks, ListingImages, ListingLogo, ListingDetails, PageHeading, Spinner, BanEditProfile, ListingButtons},
  mixins: [isBannedFromEditing, userDevice],
  data: function() {
    return {
      step: 1,
      busy: true,
      focused: {},
      coverageError: false,
      clean: true,
      nextTab: null,
      bgListing: null,
      sgListing: null,
      canSave: true,
      isSaving: false,
    }
  },
  computed: {
    listingDetails: {
      get: function() {
        return this.$store.state.company.MAIN_LISTING;
      },
      set: function(value) {
        this.$store.commit(MAIN_LISTING, value);
      }
    },
    listingMasterSwitch: function() {
      return (this.$store.state.company.COMPANY) ? this.$store.state.company.COMPANY.show_listing : true;
    },
    pageTitle() {
      return this.$t('Listing');
    }
  },
  methods: {
    save: function(step) {
      const activeTab = 'l'+step;
      this.$refs[activeTab].save();
      this.getLiveListings();
      this.clean = true;
    },
    setState: function(clean) {
      this.clean = clean;
    },
    navigate: function(forward, tab) {
      if (this.clean) {
        this.step = tab ? tab : (forward ? this.step + 1 : this.step - 1);
        this.nextTab = null;
      } else {
        this.nextTab = tab ? tab : (forward ? this.step + 1 : this.step - 1);
        this.$bvModal.show('nav-confirm');
      }
    },
    setCanSave: function(value) {
      this.canSave = value;
    },
    setIsSaving: function(value) {
      this.isSaving = value;
    },
    getLiveListings: function() {
      this.$api.company.getLiveListings().then((response) => {
        this.bgListing = response.data.data.listings['boilerguide.co.uk'];
        this.sgListing = response.data.data.listings['solarguide.co.uk'];
      });
    }
  },
  created: function() {
    if (!this.isBannedFromEditing) {
      this.$store.dispatch('getMaxUploadSize');
      this.busy = true;
      this.$store.dispatch('getListing')
        .then(() => {
          this.getLiveListings();
        })
        .catch(() => {
          this.coverageError = true;
        })
        .finally(() => {
          this.busy = false;
        })
    }
  },
}
</script>

<style scoped lang="scss">
.listing-steps {
  @include media-breakpoint-down(md) {
    width: 100%;
  }

  &__tab {
    cursor: pointer;
    transition: $transition-base;
    box-shadow: inset 0 0 0 1px $border-color;
    text-align: left;
    text-decoration: none;
    display: flex;
    justify-content: space-between;
    align-items: center;

    @media only screen and (max-width: 374px) {
      padding: 10px 5px;
    }

    @media only screen and (min-width: 375px) and (max-width: 767px) {
      padding: 16px 10px;
    }

    @include media-breakpoint-up(md) {
      padding: 16px;
    }

    @media only screen and (min-width: 1200px) and (max-width: 1260px) {
      max-width: 120px;
    }

    &:hover {
      background: $theme-secondary-accents-900;
      box-shadow: inset 0 0 0 2px $theme-primary;
    }

    &--selected {
      background-color: $theme-secondary-accents-900;
      box-shadow: inset 0 0 0 2px $theme-primary;
    }

    &.first {
      border-radius: 6px 0 0 6px;
    }

    @include media-breakpoint-down(md) {

      &.last {
        border-radius: 0 6px 6px 0;
      }
    }

    @include media-breakpoint-between(sm, md) {
      width: 25%;
    }
  }
}

@include media-breakpoint-up(lg) {
  .listing-control-bar {
    border-top: 1px solid;
    border-bottom: 1px solid;
    border-right: 1px solid;
    border-color: $border-color;
    border-radius: 0 6px 6px 0;
    padding-right: 0;
  }
}

@include media-breakpoint-down(md) {
  .listing-control-bar {
    bottom: 0;
    left: 0;
    background: $white;
    width: 100%;
    padding: 10px 5px;
    position: fixed;
    box-shadow: $box-shadow;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 3;
    margin: 0;
    min-height: 60px;
  }
}

@include media-breakpoint-down(sm) {
  ::v-deep .nav__sticky-footer {
    .listing {
      padding-left: 0;
      padding-right: 0;
    }
  }
}
</style>
